import { differenceInCalendarDays, parseISO } from 'date-fns'
import { toNumber } from './decimal'
import type { DistributionLine, Invoice, InvoiceStatus, PayoutStage } from './invoice-types'

/**
 * Waar een uitbetaalregel op wacht, en dus wie er aan zet is. Negen standen, er
 * is er altijd exact een van toepassing.
 *
 * Dit is de enige bron van uitbetaal-vocabulaire, zoals invoice-status-copy.ts
 * dat is voor factuurstatussen. De schermen bevatten geen statuslogica meer:
 * label, kleur, rand en sorteervolgorde komen allemaal hiervandaan.
 */
export type WaitingState =
  | 'awaiting-incoming'
  | 'declined'
  | 'awaiting-approval'
  | 'awaiting-payout'
  | 'awaiting-client'
  | 'auto-settled'
  | 'not-sent'
  | 'paid-out'
  | 'cancelled'

/** `muted` betekent: hier is niets te doen. Dat geldt aan beide uiteinden van de levensloop. */
export type WaitingTone = 'attention' | 'neutral' | 'muted'

/** Bepaalt alleen de aanspreekvorm, nooit welke stand eruit komt. */
export type PayoutViewer = 'admin' | 'self'

/**
 * De velden die de stand bepalen. Alleen de eerste drie zijn nodig; de rest
 * verrijkt het label of de tooltip. Zo voldoen zowel de uitbetaalrijen als de
 * verdeelregels op de factuurdetailpagina en de dashboardregels hieraan.
 */
export interface WaitingLine {
  invoiceStatus: InvoiceStatus
  payoutStage: PayoutStage
  autoSettle: boolean
  invoicePaidAt?: string | null
  payoutPaidAt?: string | null
  incomingInvoiceApprovedAt?: string | null
  incomingInvoiceApprovedByName?: string | null
  incomingInvoiceDeclinedAt?: string | null
  incomingInvoiceDeclinedByName?: string | null
  incomingInvoiceDeclineReason?: string | null
}

/** De verdeelregel draagt de uitbetaalvelden, de factuur eromheen de context. */
export function waitingLineOf(invoice: Invoice, line: DistributionLine): WaitingLine {
  return {
    invoiceStatus: invoice.status,
    invoicePaidAt: invoice.paidAt,
    payoutStage: line.payoutStage,
    payoutPaidAt: line.payoutPaidAt,
    autoSettle: line.autoSettle,
    incomingInvoiceApprovedAt: line.incomingInvoiceApprovedAt,
    incomingInvoiceApprovedByName: line.incomingInvoiceApprovedByName,
  }
}

export interface WaitingOn {
  state: WaitingState
  tone: WaitingTone
  /** De zin in de kolom "Wacht op". Staat op zichzelf: ook zonder kolomkop leesbaar. */
  label: string
  /** Verduidelijkt; bevat nooit informatie die je alleen daar kunt vinden. */
  hint: string | null
  dot: string
  text: string
  border: string
  /** Sorteervolgorde, laag = eerst. */
  order: number
}

interface StateMeta {
  tone: WaitingTone
  dot: string
  text: string
  border: string
  order: number
}

/**
 * De presentatie per stand. De dots hergebruiken de factuurstatus-familie zodat
 * het scherm geen tweede kleurtaal introduceert. Alleen de twee attentie-standen
 * voeren hun kleur ook in de tekst; de rest blijft ink-soft.
 */
const STATE: Record<WaitingState, StateMeta> = {
  'awaiting-incoming': {
    tone: 'attention',
    dot: 'bg-status-overdue',
    text: 'text-status-overdue',
    border: 'border-l-status-overdue',
    order: 0,
  },
  declined: {
    tone: 'attention',
    dot: 'bg-status-declined',
    text: 'text-status-declined',
    border: 'border-l-status-declined',
    order: 1,
  },
  'awaiting-approval': {
    tone: 'neutral',
    dot: 'bg-status-approval',
    text: 'text-ink-soft',
    border: 'border-l-transparent',
    order: 2,
  },
  'awaiting-payout': {
    tone: 'neutral',
    dot: 'bg-status-approved',
    text: 'text-ink-soft',
    border: 'border-l-transparent',
    order: 3,
  },
  'awaiting-client': {
    tone: 'neutral',
    dot: 'bg-status-sent',
    text: 'text-ink-soft',
    border: 'border-l-transparent',
    order: 4,
  },
  'auto-settled': {
    tone: 'neutral',
    dot: 'bg-status-approved',
    text: 'text-ink-soft',
    border: 'border-l-transparent',
    order: 5,
  },
  'not-sent': {
    tone: 'muted',
    dot: 'bg-status-concept',
    text: 'text-ink-soft',
    border: 'border-l-transparent',
    order: 6,
  },
  'paid-out': {
    tone: 'muted',
    dot: 'bg-status-paid',
    text: 'text-ink-soft',
    border: 'border-l-transparent',
    order: 7,
  },
  cancelled: {
    tone: 'muted',
    dot: 'bg-status-closed',
    text: 'text-ink-soft',
    border: 'border-l-transparent',
    order: 8,
  },
}

/**
 * Wat de factuurstand betekent voor de uitbetaling. Een Record dwingt af dat een
 * nieuwe factuurstatus hier langs moet: de build breekt anders. Dat is de
 * bedoeling, want een onbekende stand stilzwijgend als gedempt tonen verbergt werk.
 */
const BY_INVOICE: Record<InvoiceStatus, 'not-sent' | 'awaiting-client' | 'paid' | 'cancelled'> = {
  DRAFT: 'not-sent',
  PARKED: 'not-sent',
  PENDING_APPROVAL: 'not-sent',
  APPROVED: 'not-sent',
  SENT: 'awaiting-client',
  ESCALATED: 'awaiting-client',
  PAID: 'paid',
  WITHDRAWN: 'cancelled',
  WRITTEN_OFF: 'cancelled',
  VOIDED: 'cancelled',
  REPLACED: 'cancelled',
}

/**
 * De precedentie, van hard naar zacht:
 *
 *  1. Uitbetaald wint van alles: ook op een ingetrokken factuur is uitbetaald uitbetaald.
 *  2. Komt het geld nooit binnen, dan wacht de regel nergens meer op.
 *  3. Afgekeurd gaat voor de factuurstand: er moet nu iets gebeuren, ook als de
 *     klant nog niet betaald heeft. Dit is de enige fase die de factuurstand overruled.
 *  4. Zonder geld van de klant kan er niets uitbetaald worden, dus daarna gaat de
 *     factuurstand voor de rest van de fase. Een vroeg ingediende inkoopfactuur op
 *     een verzonden factuur leest dus als "wacht op betaling door klant"; dat de
 *     factuur er al ligt zie je in de kolom Inkoopfactuur, waar het bestand staat.
 */
function stateOf(line: WaitingLine): WaitingState {
  if (line.payoutStage === 'BETAALD') return 'paid-out'

  const invoice = BY_INVOICE[line.invoiceStatus]
  if (invoice === 'cancelled') return 'cancelled'
  if (line.payoutStage === 'AFGEKEURD') return 'declined'
  if (invoice !== 'paid') return invoice

  if (line.autoSettle) return 'auto-settled'
  if (line.payoutStage === 'INGEDIEND') return 'awaiting-approval'
  if (line.payoutStage === 'GEACCORDEERD') return 'awaiting-payout'
  return 'awaiting-incoming'
}

/**
 * De kale tekst per stand. Elk label noemt de partij die aan zet is.
 *
 * De viewer regelt alleen de aanspreekvorm waar die verschilt: op je eigen
 * uitbetalingen is de tegenpartij het antwoord op de vraag, op het overzicht
 * staat de ontvanger al in de groepskop erboven. Waar de partij aan zet CW zelf
 * is, deelt elke lezer dezelfde tekst: het overzicht is ook voor een partner
 * zichtbaar, en die is daar tegenpartij en niet degene die uitbetaalt.
 */
export function stateLabel(state: WaitingState, viewer: PayoutViewer): string {
  switch (state) {
    case 'awaiting-incoming':
      return viewer === 'self' ? 'Wacht op jouw inkoopfactuur' : 'Wacht op inkoopfactuur'
    case 'declined':
      return viewer === 'self' ? 'Afgekeurd, stuur een nieuwe' : 'Afgekeurd, nieuwe factuur nodig'
    case 'awaiting-approval':
      return viewer === 'self' ? 'Wacht op accordering door ConfigureWise' : 'Wacht op accordering'
    case 'awaiting-payout':
      return 'Akkoord, CW betaalt uit'
    case 'awaiting-client':
      return 'Wacht op betaling door klant'
    case 'auto-settled':
      return 'Wordt zelf verrekend'
    case 'not-sent':
      return 'Nog niet verstuurd naar klant'
    case 'paid-out':
      return 'Uitbetaald'
    case 'cancelled':
      return 'Factuur vervallen'
  }
}

const CANCELLED: Partial<Record<InvoiceStatus, string>> = {
  WITHDRAWN: 'Factuur ingetrokken',
  WRITTEN_OFF: 'Factuur oninbaar',
  VOIDED: 'Factuur gestorneerd',
  REPLACED: 'Factuur vervangen',
}

export function dayMonth(iso: string): string {
  return `${iso.slice(8, 10)}-${iso.slice(5, 7)}`
}

function daysSince(iso: string): number {
  return Math.max(0, differenceInCalendarDays(new Date(), parseISO(iso)))
}

/**
 * Verrijkt het label met de regelcontext. Bij een ontbrekende inkoopfactuur is
 * de ouderdom de beslisrelevante maat, niet de betaaldatum: die staat in de hint.
 */
function decorate(state: WaitingState, line: WaitingLine, viewer: PayoutViewer): string {
  const base = stateLabel(state, viewer)
  switch (state) {
    case 'awaiting-incoming': {
      const days = line.invoicePaidAt ? daysSince(line.invoicePaidAt) : 0
      return days > 0 ? `${base} · ${days}d` : base
    }
    case 'awaiting-client':
      return line.invoiceStatus === 'ESCALATED' ? 'Aangemaand, wacht op klant' : base
    case 'paid-out':
      return line.payoutPaidAt ? `Uitbetaald ${dayMonth(line.payoutPaidAt)}` : base
    case 'cancelled':
      return CANCELLED[line.invoiceStatus] ?? base
    default:
      return base
  }
}

/** Alleen waar de regel opgeslagen detail samenvat dat niet in het label past. */
function hintOf(state: WaitingState, line: WaitingLine): string | null {
  switch (state) {
    case 'awaiting-incoming': {
      if (!line.invoicePaidAt) return 'De klant heeft betaald; de inkoopfactuur is nog niet ingediend.'
      const days = daysSince(line.invoicePaidAt)
      const since = days === 0 ? 'vandaag' : days === 1 ? '1 dag geleden' : `${days} dagen geleden`
      return `Klant betaalde op ${dayMonth(line.invoicePaidAt)}, ${since}. De inkoopfactuur is nog niet ingediend.`
    }
    case 'declined': {
      const when = line.incomingInvoiceDeclinedAt ? ` op ${dayMonth(line.incomingInvoiceDeclinedAt.slice(0, 10))}` : ''
      const who = line.incomingInvoiceDeclinedByName ? ` door ${line.incomingInvoiceDeclinedByName}` : ''
      const why = line.incomingInvoiceDeclineReason ? `: ${line.incomingInvoiceDeclineReason}` : ''
      return `Afgekeurd${when}${who}${why}`
    }
    case 'awaiting-payout': {
      if (!line.incomingInvoiceApprovedByName) return null
      const when = line.incomingInvoiceApprovedAt ? ` op ${dayMonth(line.incomingInvoiceApprovedAt)}` : ''
      return `Geaccordeerd door ${line.incomingInvoiceApprovedByName}${when}`
    }
    default:
      return null
  }
}

export function waitingOn(line: WaitingLine, viewer: PayoutViewer = 'admin'): WaitingOn {
  const state = stateOf(line)
  return { state, ...STATE[state], label: decorate(state, line, viewer), hint: hintOf(state, line) }
}

/** Wat vastloopt bovenaan, wat af is onderaan. */
export function isStuck(line: WaitingLine): boolean {
  return STATE[stateOf(line)].tone === 'attention'
}

/**
 * De klant heeft betaald en de inkoopfactuur ligt er: nu is CW aan zet, met
 * accorderen of uitbetalen. De tegenhanger van isStuck, waar de ontvanger aan
 * zet is. De twee sluiten elkaar uit; een afgekeurde regel wacht op een nieuwe
 * factuur en telt dus niet mee.
 */
export function isPayable(line: WaitingLine): boolean {
  const state = stateOf(line)
  return state === 'awaiting-approval' || state === 'awaiting-payout'
}

/**
 * Binnen een bucket blijft de volgorde van de server staan (factuurnummer
 * oplopend, dus langst wachtend eerst); Array.sort is stabiel.
 */
export function sortPayoutLines<T extends WaitingLine>(lines: T[]): T[] {
  return [...lines].sort((a, b) => STATE[stateOf(a)].order - STATE[stateOf(b)].order)
}

export interface PayoutSummary {
  count: number
  amount: number
}

/** Hoeveel regels en hoeveel geld er in een doorsnede zitten. */
export function summarize(
  lines: (WaitingLine & { amount: string })[],
  predicate: (line: WaitingLine) => boolean,
): PayoutSummary {
  return lines.filter(predicate).reduce<PayoutSummary>(
    (acc, line) => ({ count: acc.count + 1, amount: acc.amount + toNumber(line.amount) }),
    { count: 0, amount: 0 },
  )
}
